import { NAMED_COLORS } from '../../lib/color';
import { GradientParser } from '../../lib/gradient';
import seed from '../../lib/seed-random';
import { hashStr } from '../../common-utils';
import { FILL_TYPES } from '../constants';
import { renderImageWithContent } from '../image';
import { getNodeImageCache, loadImageAsync } from '../../provider/image';
import { normalizeDimensions, renderRect } from '../shape';
import { disposeImage } from './image';
import { resolveRadius } from './radius';
import { getCacheByType } from './raster';
import { isGradientPatternFill, createGradientPattern } from './gradient-pattern';

seed();

/**
 * Determines the type of fill based on the provided value
 * @param {string|object} fill - The fill value to analyze
 * @returns {string} - The type of fill: 'gradient', 'image', 'color', 'pattern', or 'none'
 */
export const resolveFillType = (fill) => {
  // Handle null, undefined or non-string values
  if (!fill || typeof fill !== 'string') return FILL_TYPES.NONE;

  if (fill.startsWith('dynamic-shader')) return FILL_TYPES.DYNAMIC;

  // Handle gradient pattern (gradient + background-size for tiled patterns)
  // This must be checked before regular gradient to ensure proper handling
  if (isGradientPatternFill(fill)) return FILL_TYPES.GRADIENT_PATTERN;

  // Handle gradient types
  if (
    fill.startsWith('linear-gradient') ||
    fill.startsWith('radial-gradient') ||
    fill.startsWith('conic-gradient')
  )
    return FILL_TYPES.GRADIENT;

  // Handle image URLs
  if (fill.startsWith('url') || fill.startsWith('asset://') || fill.startsWith('https://'))
    return FILL_TYPES.IMAGE;

  // Handle color formats
  if (
    fill.startsWith('rgba') ||
    fill.startsWith('rgb') ||
    fill.startsWith('#') ||
    fill.startsWith('hsl') ||
    fill.startsWith('hsla')
  )
    return FILL_TYPES.COLOR;

  // Handle named colors
  if (NAMED_COLORS[fill.toLowerCase()]) return FILL_TYPES.COLOR;

  // Handle pattern fills
  if (fill.startsWith('pattern')) return FILL_TYPES.PATTERN;

  return FILL_TYPES.NONE;
};

export function parseCSSURLString(value) {
  if (!value || typeof value !== 'string') return null;
  // Accept direct URLs without url() wrapper
  if (
    value.startsWith('asset://') ||
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('data:') ||
    value.startsWith('/')
  ) {
    return value;
  }

  // Match url() pattern with optional quotes
  const urlMatch = value.match(/url\(["']?(.*?)["']?\)/);
  if (!urlMatch) return null;

  // Extract the URL and remove any remaining quotes
  let url = urlMatch[1];
  if (url.startsWith('"') || url.startsWith("'")) {
    url = url.slice(1, -1);
  }

  // Return the cleaned URL
  return url;
}

export function drawBackground(renderContext, node) {
  const mainContext = renderContext.context;
  const size = { w: node.w, h: node.h };
  const id = node.id;
  let { fill, radius } = node;
  if (!fill) return;

  // 兼容之前的设定
  if (fill instanceof Object) {
    if (fill.fill?.fill) {
      fill = fill.fill.fill;
    }
  }

  // Check for advanced features like noise, blur, and overlay
  const hasBlur = node.blur && node.blur > 0;
  const hasOverlay =
    node.overlay && (node.overlay.id || Object.keys(node.overlay.params || {}).length > 0);
  const fillType = resolveFillType(fill);
  const isImage = fillType === 'image';
  const hasEffect = hasBlur || hasOverlay || isImage;
  // If we have any advanced features, we need to ensure proper rendering
  if (hasEffect) {
    // Log for debugging purposes
    if (renderContext.debug) {
      console.log(`Advanced background features detected for node ${id}:`, {
        blur: node.blur,
        overlay: node.overlay,
      });
    }
  } else {
    renderRect(renderContext, {
      ...node,
      x: 0,
      y: 0,
    });
    return;
  }

  const context = getBackgroundContext(renderContext, id, size);
  // include image readiness in cache key to avoid caching blank state
  let imageSignature = '';
  // keep a per-render local handle
  let bgImageHandle = null;
  if (fillType === 'image') {
    const handle = getNodeImageCache(renderContext, node.id);
    bgImageHandle = handle;
    const status = handle ? (handle.loading ? 'L' : handle.img ? 'R' : 'U') : 'U';
    const cacheKeyPart = handle?.cacheKey || '';
    const dim = handle?.img ? `${handle.img.width}x${handle.img.height}` : '';
    imageSignature = `:I${status}:${cacheKeyPart}:${dim}`;
  }

  const nextCacheKey = `background:${node.blur || 0}:${hashStr(fill || '')}:${node.overlay?.id || ''}:${JSON.stringify(node.overlay?.params || {})}:${size.w}:${size.h}${imageSignature}`;
  const isExport = renderContext.mode === 'export';
  if (!isExport && nextCacheKey === context.cacheKey) {
    mainContext.drawImage(context.canvas, 0, 0, size.w, size.h);
    return;
  }

  // If background image not ready, preserve previous cache to avoid blank
  if (fillType === 'image' && !(bgImageHandle && bgImageHandle.img)) {
    if (!isExport && context.cacheKey) {
      mainContext.drawImage(context.canvas, 0, 0, size.w, size.h);
      return;
    }
  }

  context.clearRect(0, 0, size.w, size.h);

  if (fillType === 'gradient') {
    if (GradientParser.is(fill)) {
      try {
        const rect = {
          x: 0,
          y: 0,
          w: size.w,
          h: size.h,
        };
        const gradient = GradientParser.create(context, fill, rect);
        context.fillStyle = gradient;
      } catch (e) {
        console.error('fill', fill, e);
      }
    }
  } else if (fillType === FILL_TYPES.GRADIENT_PATTERN) {
    // Handle gradient pattern (gradient + background-size for tiled patterns)
    const pattern = createGradientPattern(context, fill, size);
    if (pattern) {
      context.fillStyle = pattern;
    }
  } else if (fillType === 'image') {
    const img = bgImageHandle || getNodeImageCache(renderContext, node.id);
    if (img && img.img) {
      // Create a temporary node for rendering with image cache stored in provider
      const imageNodeId = `__bg_${node.id}`;
      const imageProvider = renderContext.providers.image;
      if (imageProvider) {
        imageProvider.setImageCache(imageNodeId, img);
      }
      const imageNode = {
        id: imageNodeId,
        x: 0,
        y: 0,
        w: size.w,
        h: size.h,
        src: 'asset://frame',
        fit: node.fit || 'cover',
        clip: node.clip,
        radius: radius,
      };
      renderImageWithContent(context, renderContext, imageNode, radius);
    } else {
      // image not ready: fallback solid fill to avoid transparent cache
      context.fillStyle = node.backgroundColor || 'white';
    }
  } else if (fillType === 'color') {
    context.fillStyle = fill;
  } else {
    context.fillStyle = 'white';
  }

  const shouldFill = !isImage || !(bgImageHandle && bgImageHandle.img);
  if (shouldFill) {
    // 绘制渐变背景
    if (radius) {
      drawRoundedRect(context, 0, 0, size.w, size.h, radius);
    } else {
      context.fillRect(0, 0, size.w, size.h);
    }
  }

  drawOverlay(renderContext, node, size);

  drawBlur(renderContext, node, size);

  // update cache key only after we drew new content to avoid caching blank state
  context.cacheKey = nextCacheKey;
  mainContext.drawImage(context.canvas, 0, 0, size.w, size.h);
}

function drawRoundedRect(context, x, y, width, height, radius) {
  const {
    x: normX,
    y: normY,
    w: normW,
    h: normH,
  } = normalizeDimensions({ x, y, w: width, h: height });

  if (!radius) {
    context.rect(normX, normY, normW, normH);
    context.fill();
    return;
  }

  const { topLeft, topRight, bottomRight, bottomLeft } = resolveRadius(normW, normH, radius);

  context.beginPath();
  context.moveTo(normX + topLeft, normY);
  context.lineTo(normX + normW - topRight, normY);
  context.arcTo(normX + normW, normY, normX + normW, normY + topRight, topRight);
  context.lineTo(normX + normW, normY + normH - bottomRight);
  context.arcTo(
    normX + normW,
    normY + normH,
    normX + normW - bottomRight,
    normY + normH,
    bottomRight
  );
  context.lineTo(normX + bottomLeft, normY + normH);
  context.arcTo(normX, normY + normH, normX, normY + normH - bottomLeft, bottomLeft);
  context.lineTo(normX, normY + topLeft);
  context.arcTo(normX, normY, normX + topLeft, normY, topLeft);
  context.closePath();
  context.fill();
}

function getBackgroundContext(renderContext, id, size) {
  const caches = getCacheByType(renderContext, 'background');
  let backgroundContext = caches.get(id);

  if (!backgroundContext) {
    const canvas = document.createElement('canvas');
    canvas.width = size.w;
    canvas.height = size.h;
    backgroundContext = canvas.getContext('2d');
    caches.set(id, backgroundContext);
  }

  if (backgroundContext.canvas.width !== size.w || backgroundContext.canvas.height !== size.h) {
    backgroundContext.canvas.width = size.w;
    backgroundContext.canvas.height = size.h;
  }
  return backgroundContext;
}

function drawBlur(renderContext, node, size) {
  const blur = node.blur || 0;
  const id = node.id;
  if (!blur) return;
  const context = getBackgroundContext(renderContext, id, size);
  const blurCanvas = document.createElement('canvas');
  blurCanvas.width = context.canvas.width;
  blurCanvas.height = context.canvas.height;
  const blurCtx = blurCanvas.getContext('2d');

  // Apply the blur effect using a different approach
  blurCtx.filter = `blur(${blur}px)`;
  blurCtx.drawImage(context.canvas, 0, 0, blurCanvas.width, blurCanvas.height);

  // Apply a second pass of blur for better effect
  const blurCanvas2 = document.createElement('canvas');
  blurCanvas2.width = context.canvas.width;
  blurCanvas2.height = context.canvas.height;
  const blurCtx2 = blurCanvas2.getContext('2d');
  blurCtx2.filter = `blur(${blur / 2}px)`;
  blurCtx2.drawImage(blurCanvas, 0, 0, blurCanvas2.width, blurCanvas2.height);

  context.globalAlpha = 1;
  context.drawImage(blurCanvas2, 0, 0, size.w, size.h);
}

function drawImagePatternOverlay(renderContext, context, node, size) {
  const overlay = node.overlay;
  const id = node.id;
  if (!overlay) return;
  let svgString = overlay.image;
  Object.keys(overlay.params).map((key) => {
    svgString = svgString.replaceAll(`{{${key}}}`, encodeURIComponent(overlay.params[key] || ''));
  });
  const preImg = node._overlay;
  if (preImg && preImg.src !== svgString) {
    disposeImage(renderContext, node, '', { cacheKey: preImg.cacheKey, key: '_overlay' });
  }
  const img = loadImageAsync(
    renderContext,
    svgString,
    () => {
      context.cacheKey = '';
    },
    { id }
  );
  const renderImg = img.img || preImg?.img;
  node._overlay = img;
  if (renderImg) {
    // Create offscreen canvas to control pattern size
    const patternCanvas = document.createElement('canvas');
    const patternSize = overlay.params.size || 50; // Default size if not specified
    patternCanvas.width = patternSize;
    patternCanvas.height = patternSize;
    const patternCtx = patternCanvas.getContext('2d');

    // Draw image to pattern canvas with desired size
    patternCtx.drawImage(renderImg, 0, 0, patternSize, patternSize);

    if (overlay.type === 'cover') {
      context.drawImage(renderImg, 0, 0, size.w, size.h);
    } else {
      // Create pattern from resized canvas
      const pattern = context.createPattern(patternCanvas, overlay.repetition || 'repeat');
      context.fillStyle = pattern;
      context.fillRect(0, 0, size.w, size.h);
    }
  }
}

function drawOverlay(renderContext, node, size) {
  const { overlay } = node;
  if (!overlay) return;
  if (overlay.id === 'none') return;
  const id = node.id;
  const context = getBackgroundContext(renderContext, id, size);

  const isAbstract = overlay.pattern === 'circle';
  if (isAbstract) return drawAbstract(renderContext, context, node, size);
  if (overlay.pattern === 'svg') return drawImagePatternOverlay(renderContext, context, node, size);

  if (overlay && overlay.pattern) {
    context.save();
    context.beginPath();
    context.rect(0, 0, size.w, size.h);
    context.closePath();
    context.clip();

    context.globalAlpha = overlay.opacity;
    context.fillStyle = overlay.fill;
    context.strokeStyle = overlay.fill;

    const { blurRadius } = overlay;
    const overlaySize = overlay.size || 1;
    if (blurRadius) {
      const colorW = size.w - 50;
      const colorH = size.h - 50;
      const gradient = context.createRadialGradient(
        colorW / 2,
        colorH / 2,
        0,
        colorW / 2,
        colorH / 2,
        (colorW / 2) * (1 - blurRadius)
      );
      // 添加颜色和透明度的断点
      try {
        gradient.addColorStop(0, overlay.fill || 'rgba(255, 255, 255, 0)'); // 中心不透明
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)'); // 边缘透明
      } catch (e) {
        console.error(e);
      }
      // 应用渐变填充Canvas
      context.fillStyle = gradient;
      context.strokeStyle = gradient;
    } else {
      context.fillStyle = overlay.fill;
      context.strokeStyle = overlay.fill;
    }

    context.lineWidth = 2 * overlaySize;

    const gridSize = 50 * overlaySize;
    const dotsGap = 24 * overlaySize;
    const dotSize = 4 * overlaySize;
    switch (overlay.pattern) {
      case 'grid':
        for (let i = 0; i < size.w; i += gridSize) {
          for (let j = 0; j < size.h; j += gridSize) {
            context.beginPath();
            context.rect(i, j, gridSize, gridSize);
            context.stroke();
          }
        }
        break;
      case 'dots': {
        const gap = dotsGap;
        const delta = 1 * Math.sqrt(2);
        for (let j = 0; j < size.h + gap; j += gap) {
          for (let i = 0; i < size.w + gap; i += gap) {
            const deltaY = (i / gap) % 2 === 0 ? gap / delta : 0;
            context.beginPath();
            context.arc(i, j + deltaY, dotSize, 0, 2 * Math.PI);
            context.fill();
          }
        }
        break;
      }
      case 'waves': {
        const waveHeight = 28 * overlaySize;
        const waveWidth = 45 * overlaySize;
        const waveCount = Math.ceil(size.h / waveHeight);
        for (let i = 0; i < waveCount; i++) {
          context.beginPath();
          context.moveTo(0, i * waveHeight);
          for (let x = 0; x < size.w; x++) {
            const y = (Math.sin(x / waveWidth) * waveHeight) / 2 + i * waveHeight;
            context.lineTo(x, y);
          }
          context.lineTo(size.w, i * waveHeight);
          context.lineTo(size.w, size.h);
          context.lineTo(0, size.h);
          context.stroke();
        }
        break;
      }
      default:
        break;
    }
    context.restore();
    context.globalAlpha = 1;
  }
}

export function drawAbstract(renderContext, context, node, size) {
  const w = size.w;
  const h = size.h;
  const radius = Math.round(h / 2);
  const fill = null;
  // the following code is commented because it has problems:
  // const fill = GradientParser.is(background.fill.fill)
  //     ? tryGetColor(background.fill.fill)
  //     : background.fill.fill
  if (!fill) return;
  const bgcolor = fill;
  const color = colorDarken(fill, -30);

  context.save();

  context.beginPath();
  context.rect(0, 0, w, h);
  context.fillStyle = bgcolor;
  context.fill();
  context.clip();

  context.beginPath();
  context.rect(0, 0, w * 0.5, h);
  context.fillStyle = color;
  context.fill();

  drawCircle(context, w * 0.5 - radius * 2, radius * 2, radius, color, bgcolor);

  drawCircle(context, w * 0.5, 0, radius, color, bgcolor);

  drawCircle(context, w * 0.5, radius * 2, radius, color, bgcolor);

  context.restore();
}

export function colorDarken(color, percent) {
  const num = parseInt(color.slice(1), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const B = ((num >> 8) & 0x00ff) - amt;
  const G = (num & 0x0000ff) - amt;
  return `#${(0x1000000 + (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 + (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 + (G < 255 ? (G < 1 ? 0 : G) : 255)).toString(16).slice(1)}`;
}

function drawCircle(context, x, y, radius, fillStyle, bgFill) {
  context.beginPath();
  context.arc(x, y, radius, 0, Math.PI * 2, true);
  const gradient = context.createRadialGradient(x, y, 0, x, y, radius);
  gradient.addColorStop(0, fillStyle);
  gradient.addColorStop(0.716, fillStyle);
  gradient.addColorStop(1, bgFill);
  context.fillStyle = gradient;
  context.fill();
}
